import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { queryClient } from 'utils/queryClient'

export const tableClassNames = {
  th: 'bg-gray-100 shadow-xs border-b border-gray-400 h-7 font-geist-mono text-black uppercase font-bold rounded-none!',
  base: 'w-full relative',
  wrapper: 'p-0 overflow-visible relative after:content-[""] after:absolute after:top-[20px] after:right-[-14px] after:bottom-[-18px] after:left-[16px] after:bg-primary-500',
  table: 'bg-white relative z-10 border-2 border-black',
  tr: 'border-b border-gray-200 last:border-b-0',
}

export function useSchemas() {
  return useQuery({
    queryKey: ['citationSchemas'],
    queryFn: async () => {
      const { data } = await $fetch('/v1/citation_schemas/list')
      return data
    },
    staleTime: Infinity,
  })
}

export function useSchema(id) {
  return useQuery({
    queryKey: ['citationSchema', id],
    queryFn: async () => {
      const { data } = await $fetch('/v1/citation_schemas', {
        params: {
          id,
        },
      })
      return data
    },
    enabled: !!id,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  })
}

export function useCreateSchema() {
  const [searchParams, setSearchParams] = useSearchParams()
  return useMutation({
    mutationFn: async (schema) => {
      const { data } = await $fetch('/v1/citation_schemas', {
        method: 'POST',
        body: schema,
      })
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['citationSchemas'], (old) => {
        return [...old, data]
      })
      toast.success('Schema created successfully')
      searchParams.set('schema', data.id)
      setSearchParams(searchParams)
    },
    onError: (error) => {
      console.error(error)
      toast.error('Failed to create schema')
    },
  })
}

export function useDeleteSchema() {
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await $fetch('/v1/citation_schemas', {
        method: 'DELETE',
        params: {
          id,
        },
      })
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['citationSchemas'], (old) => {
        return old.filter(schema => schema.id !== data.id)
      })
      toast.success('Schema deleted successfully')
    },
    onError: (error) => {
      console.error(error)
      toast.error('Failed to delete schema')
    },
  })
}

export function usePatchSchema() {
  return useMutation({
    mutationFn: async (schema) => {
      const { data } = await $fetch('/v1/citation_schemas', {
        method: 'PATCH',
        params: {
          id: schema.id,
        },
        body: schema,
      })
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['citationSchema', data.id], data)
      queryClient.setQueryData(['citationSchemas'], (old) => {
        return old.map(schema => schema.id === data.id ? data : schema)
      })
    },
    onError: (error) => {
      console.error(error)
    },
  })
}

export function useSchemaAnalytics(schemaId, dateRange) {
  return useQuery({
    queryKey: ['citation-schema-analytics', schemaId, dateRange],
    queryFn: async () => {
      const params = {}
      if (dateRange?.start && dateRange?.end) {
        params.start_date = dateRange.start.toString()
        params.end_date = dateRange.end.toString()
      }

      const { data } = await $fetch('/v1/citation_schemas/analytics', {
        params: { id: schemaId, ...params },
      })

      return data
    },
    enabled: !!schemaId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useSchemaTimeSeries(schemaId, dateRange, interval = 'daily') {
  return useQuery({
    queryKey: ['citation-schema-time-series', schemaId, dateRange, interval],
    queryFn: async () => {
      const { data } = await $fetch('/v1/citation_schemas/analytics/time-series', {
        params: {
          id: schemaId,
          start_date: dateRange.start.toString(),
          end_date: dateRange.end.toString(),
          interval,
        },
      })
      return data
    },
    enabled: !!schemaId && !!dateRange?.start && !!dateRange?.end,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useSchemaTopValues(schemaId, variableName, limit = 10, dateRange) {
  return useQuery({
    queryKey: ['citation-schema-top-values', schemaId, variableName, limit, dateRange],
    queryFn: async () => {
      const params = { id: schemaId, variable_name: variableName, limit }
      if (dateRange?.start && dateRange?.end) {
        params.start_date = dateRange.start.toString()
        params.end_date = dateRange.end.toString()
      }

      const { data } = await $fetch('/v1/citation_schemas/analytics/top-values', {
        params,
      })
      return data?.top_values
    },
    enabled: !!schemaId && !!variableName,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useArrayDistribution(schemaId, variableName, dateRange, interval = 'daily') {
  return useQuery({
    queryKey: ['citation-schema-array-distribution', schemaId, variableName, dateRange, interval],
    queryFn: async () => {
      const { data } = await $fetch('/v1/citation_schemas/analytics/array-distribution', {
        params: {
          id: schemaId,
          variable_name: variableName,
          start_date: dateRange.start.toString(),
          end_date: dateRange.end.toString(),
          interval,
        },
      })
      return data
    },
    enabled: !!schemaId && !!variableName && !!dateRange?.start && !!dateRange?.end,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useIssuesAnalytics(schemaId, dateRange) {
  return useQuery({
    queryKey: ['citation-schema-issues', schemaId, dateRange],
    queryFn: async () => {
      const params = { id: schemaId }
      if (dateRange?.start && dateRange?.end) {
        params.start_date = dateRange.start.toString()
        params.end_date = dateRange.end.toString()
      }

      const { data } = await $fetch('/v1/citation_schemas/analytics/issues', {
        params,
      })
      return data
    },
    enabled: !!schemaId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useIssuesTimeSeries(schemaId, dateRange, interval = 'daily') {
  return useQuery({
    queryKey: ['citation-schema-issues-time-series', schemaId, dateRange, interval],
    queryFn: async () => {
      const { data } = await $fetch('/v1/citation_schemas/analytics/issues/time-series', {
        params: {
          id: schemaId,
          start_date: dateRange.start.toString(),
          end_date: dateRange.end.toString(),
          interval,
        },
      })
      return data
    },
    enabled: !!schemaId && !!dateRange?.start && !!dateRange?.end,
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: keepPreviousData,
  })
}
